'use client';

import * as React from 'react';

import { cn } from '@/core/lib/utils';

interface DialogContextType {
  open: boolean;
  setOpen: (open: boolean) => void;
  showCloseButton: boolean;
  // close popup when clicking outside
  closeOnOverlayClick: boolean;
}

const DialogContext = React.createContext<DialogContextType | undefined>(
  undefined
);

const useDialog = () => {
  const context = React.useContext(DialogContext);
  if (!context) {
    throw new Error('useDialog must be used within a Dialog');
  }
  return context;
};

interface DialogProps {
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  showCloseButton?: boolean;
  closeOnOverlayClick?: boolean;
  children: React.ReactNode;
}

const Dialog = ({
  open = false,
  onOpenChange,
  showCloseButton = true,
  closeOnOverlayClick = true,
  children,
}: DialogProps) => {
  const [internalOpen, setInternalOpen] = React.useState(open);
  const isControlled = open !== undefined;
  const currentOpen = isControlled ? open : internalOpen;
  const setCurrentOpen = isControlled ? onOpenChange! : setInternalOpen;

  React.useEffect(() => {
    if (isControlled) {
      setInternalOpen(open);
    }
  }, [open, isControlled]);

  return (
    <DialogContext.Provider
      value={{
        open: currentOpen,
        setOpen: setCurrentOpen,
        showCloseButton,
        closeOnOverlayClick,
      }}
    >
      {children}
    </DialogContext.Provider>
  );
};

const DialogTrigger = React.forwardRef<
  HTMLButtonElement,
  React.ButtonHTMLAttributes<HTMLButtonElement>
>(({ onClick, ...props }, ref) => {
  const { setOpen } = useDialog();

  return (
    <button
      ref={ref}
      onClick={(e) => {
        setOpen(true);
        onClick?.(e);
      }}
      data-pte-name='dialog-trigger-btn'
      {...props}
    />
  );
});
DialogTrigger.displayName = 'DialogTrigger';

const DialogPortal = ({ children }: { children: React.ReactNode }) => {
  const { open } = useDialog();
  return open ? <>{children}</> : null;
};

const DialogClose = React.forwardRef<
  HTMLButtonElement,
  React.ButtonHTMLAttributes<HTMLButtonElement>
>(({ onClick, ...props }, ref) => {
  const { setOpen } = useDialog();

  return (
    <button
      ref={ref}
      onClick={(e) => {
        setOpen(false);
        onClick?.(e);
      }}
      data-pte-name='dialog-close-btn'
      {...props}
    />
  );
});
DialogClose.displayName = 'DialogClose';

const DialogOverlay = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement>
>(({ className, ...props }, ref) => (
  <div
    ref={ref}
    className={cn(
      'fixed inset-0 z-50 bg-black/50 animate-in fade-in-0',
      className
    )}
    data-pte-name='dialog-overlay'
    {...props}
  />
));
DialogOverlay.displayName = 'DialogOverlay';

const DialogContent = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement>
>(({ className, children, ...props }, ref) => {
  const { setOpen, showCloseButton, closeOnOverlayClick } = useDialog();

  return (
    <DialogPortal>
      <DialogOverlay onClick={() => closeOnOverlayClick && setOpen(false)} />
      <div
        ref={ref}
        className={cn(
          'fixed left-[50%] top-[50%] z-50 grid w-[92%] sm:w-full max-w-lg translate-x-[-50%] translate-y-[-50%] gap-4 border bg-container-primary p-6 shadow-lg duration-200 animate-in fade-in-0 zoom-in-95 rounded-md',
          className
        )}
        data-pte-name='dialog-content'
        {...props}
      >
        {children}
        {showCloseButton && (
          <DialogClose
            className='w-8 h-8 p-1 absolute right-5 top-5 rounded-sm opacity-70 ring-offset-background transition-opacity hover:opacity-100 hover:bg-transparent focus:outline-none disabled:pointer-events-none'
            data-pte-name='dialog-close-icon-btn'
          >
            <svg
              viewBox='0 0 16 17'
              fill='none'
              xmlns='http://www.w3.org/2000/svg'
            >
              <path
                d='M11.3457 4.30493C11.6035 4.04715 12.0102 4.02605 12.2422 4.25806C12.4738 4.49013 12.452 4.89593 12.1943 5.15356L8.84863 8.49927L12.2021 11.8528C12.4599 12.1106 12.481 12.5173 12.249 12.7493C12.017 12.9809 11.6112 12.9591 11.3535 12.7014L8 9.3479L4.64941 12.6985C4.39163 12.9562 3.9859 12.9774 3.75391 12.7454C3.522 12.5134 3.54308 12.1076 3.80078 11.8499L7.15137 8.49927L3.80859 5.15649C3.551 4.8987 3.52977 4.49293 3.76172 4.26099C3.99369 4.02918 4.39948 4.05031 4.65723 4.30786L8 7.65063L11.3457 4.30493Z'
                fill='#697586'
              />
            </svg>
          </DialogClose>
        )}
      </div>
    </DialogPortal>
  );
});
DialogContent.displayName = 'DialogContent';

const DialogHeader = ({
  className,
  ...props
}: React.HTMLAttributes<HTMLDivElement>) => (
  <div
    className={cn('flex flex-col text-left', className)}
    data-pte-name='dialog-header'
    {...props}
  />
);
DialogHeader.displayName = 'DialogHeader';

const DialogFooter = ({
  className,
  ...props
}: React.HTMLAttributes<HTMLDivElement>) => (
  <div
    className={cn(
      'flex flex-row justify-end space-x-2 mt-1 text-primary-2',
      className
    )}
    data-pte-name='dialog-footer'
    {...props}
  />
);
DialogFooter.displayName = 'DialogFooter';

const DialogTitle = React.forwardRef<
  HTMLHeadingElement,
  React.HTMLAttributes<HTMLHeadingElement>
>(({ className, ...props }, ref) => (
  <h2
    ref={ref}
    className={cn(
      'text-lg font-semibold text-primary-2 leading-none tracking-tight',
      className
    )}
    data-pte-name='dialog-title'
    {...props}
  />
));
DialogTitle.displayName = 'DialogTitle';

const DialogDescription = React.forwardRef<
  HTMLParagraphElement,
  React.HTMLAttributes<HTMLParagraphElement>
>(({ className, ...props }, ref) => (
  <p
    ref={ref}
    className={cn('text-sm text-secondary', className)}
    data-pte-name='dialog-description'
    {...props}
  />
));
DialogDescription.displayName = 'DialogDescription';

export {
  Dialog,
  DialogPortal,
  DialogOverlay,
  DialogTrigger,
  DialogClose,
  DialogContent,
  DialogHeader,
  DialogFooter,
  DialogTitle,
  DialogDescription,
};
